import { api } from './api';
import { useQuery } from './useQuery';

/**
 * Who signs a laboratory result out at a venue, and whether anybody can.
 *
 * A result is validated and released by a Pathologist, and never by the
 * person who entered it (result.service.ts refuses both). A venue staffed
 * with technicians alone can therefore enter every value and release none:
 * nothing tells it so until a case is found stuck. These helpers let the
 * screens say it — the bench names whom its values wait on, and the venue's
 * readiness warns before the first employee is booked there.
 */

export interface Signer { id: string; fullName: string; isYou: boolean }

/** The venue's signers, from the bench's side. `partnerId` is omitted for a venue login. */
export function useSigners(partnerId: string | null | undefined, enabled = true) {
  return useQuery<{ partnerId: string | null; signers: Signer[] }>(
    () => (enabled
      ? api.get(`/lab/signers${partnerId ? `?partnerId=${partnerId}` : ''}`)
      : Promise.resolve({ partnerId: null, signers: [] })),
    [partnerId ?? '', enabled],
  );
}

/** "A", "A or B", "A, B or C" — whom a value waits on. */
export function eitherOf(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

interface StaffLike { id: string; status: string; fullName: string; roles: Array<{ key: string }> }

/** Venue roles, by what they do to a result. */
const SIGNS = new Set(['PATHOLOGIST']);
const ENTERS = new Set(['LAB_TECHNICIAN']);

export type SignoffGap =
  | { kind: 'NONE' }
  | { kind: 'NO_SIGNER'; enterers: number }
  | { kind: 'SIGNER_ENTERS'; signer: string };

/**
 * Whether a venue's active staff can get a result out the door.
 *
 *   · NO_SIGNER — no active Pathologist: every result stops at validation.
 *   · SIGNER_ENTERS — one Pathologist, who also enters results: whatever they
 *     enter themselves needs a second Pathologist nobody has appointed.
 */
export function signoffGap(staff: StaffLike[]): SignoffGap {
  const active = staff.filter((u) => u.status === 'ACTIVE');
  const signers = active.filter((u) => u.roles.some((r) => SIGNS.has(r.key)));
  const enterers = active.filter((u) => u.roles.some((r) => ENTERS.has(r.key)));
  if (!signers.length) return { kind: 'NO_SIGNER', enterers: enterers.length };
  if (signers.length === 1 && enterers.some((u) => u.id === signers[0]!.id)) {
    return { kind: 'SIGNER_ENTERS', signer: signers[0]!.fullName };
  }
  return { kind: 'NONE' };
}

/** The sentence for a gap, worded for whoever manages the venue's staff. */
export function signoffGapText(gap: SignoffGap): { title: string; detail: string } | null {
  switch (gap.kind) {
    case 'NO_SIGNER':
      return {
        title: 'Nobody can release results',
        detail: 'No active Pathologist login. Results can be entered here but not validated or released, so no case reaches a physician. Add a Pathologist.',
      };
    case 'SIGNER_ENTERS':
      return {
        title: 'Only one Pathologist, who also enters results',
        detail: `${gap.signer} cannot validate a value they entered themselves, and nobody else can. Add a second Pathologist, or leave entry to the technicians.`,
      };
    default:
      return null;
  }
}
